import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Card, Container, Grid2 as Grid, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React from 'react'
import { useNavigate } from 'react-router'
import { Autoplay, EffectCoverflow, FreeMode, Keyboard, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import PrivateSessionCard from '../components/cards/PrivateSessionCard'
import EventDisplay from '../components/common/EventDisplay'
import SimpleHorizontalCards from '../components/layout/SimpleHorizontalCards'
import { STATUS } from '../share/Constant'

const createSampleParticipants = (count) => {
  const hardCodeUsers = [
    { id: 'member-001', familyId: 0 },
    { id: 'member-001', familyId: 1 },
    { id: 'member-001', familyId: 2 },
  ]

  const generated = Array.from({ length: count }, (_, i) => ({
    id: `member-${String(i + 1).padStart(3, '0')}`,
    familyId: Math.floor(Math.random() * 4),
    note: '',
  }))

  return [...hardCodeUsers, ...generated]
}

export default function Demo() {
  const navigate = useNavigate()
  // Sample data

  const sampleParticipants = createSampleParticipants(5)

  const sampleEvent = {
    resourceId: '123',
    title: 'Beginner Snowboard Course',
    image:
      'https://images.pexels.com/photos/30230100/pexels-photo-30230100/free-photo-of-stunning-alpine-mountain-peak-with-clear-blue-sky.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    description: 'Perfect for beginners, starting from the basics of snowboarding.',
    details: `
# Course Content

The Level 1 Instructor course is an introductory certification aimed at equipping new instructors with the fundamental tools and training to teach beginner and novice snowboarders.

- Basic equipment usage
- Fundamental stance and balance
- Straight-line riding techniques
- Basic turning techniques

## Target Audience

- Complete beginners
- Enthusiasts wanting systematic training

## Equipment Requirements

1. Snowboard gear
2. Gloves
3. Goggles

> Equipment rental available on-site
    `,
    skiResort: 'Whistler Blackcomb',
    date: '2024-03-15',
    price: 299,
    status: 'AVAILABLE',
    sport: 'SNOWBOARD',
    instructorId: 'instructor-001',
    participants: sampleParticipants,
    participantIds: sampleParticipants.map((p) => p.id),
    maxParticipants: 6,
    minParticipants: 3,
    registrationDeadline: '2024-03-14T23:59:59Z',
    cancellationDeadline: '2024-03-13T23:59:59Z',
    registrationDeadlineHours: 24,
    cancellationDeadlineHours: 48,
    times: [
      {
        resourceId: 1,
        time: '10:00 AM - 12:00 PM',
        startTime: '10:00:00',
        endTime: '12:00:00',
      },
      {
        resourceId: 2,
        time: '2:00 PM - 4:00 PM',
        startTime: '14:00:00',
        endTime: '16:00:00',
      },
    ],
    notes: [
      {
        userId: 'member-001',
        note: 'First time snowboarding, excited!',
      },
      {
        userId: 'member-002',
        note: 'Need equipment rental',
      },
    ],
    events: [
      {
        type: 'REGISTRATION',
        timestamp: '2024-02-15T10:30:00Z',
        userId: 'member-001',
        message: 'Registered for morning session',
      },
      {
        type: 'REGISTRATION',
        timestamp: '2024-02-16T14:20:00Z',
        userId: 'member-002',
        message: 'Registered for afternoon session',
      },
    ],
    url: '/api/group-session/123',
  }

  // Sample private session data
  const samplePrivateSession = {
    resourceId: 'private-001',
    startTime: dayjs().add(1, 'day').hour(10).minute(0).second(0).toISOString(),
    endTime: dayjs().add(1, 'day').hour(12).minute(0).second(0).toISOString(),
    description: 'One-on-one snowboarding lesson for beginners',
    skiResort: 'Whistler Blackcomb',
    sport: 'SNOWBOARD',
    status: STATUS.AVAILABLE,
    instructorId: 'instructor-001',
    participantIds: ['member-001'],
    price: 199,
    location: 'Whistler Blackcomb',
  }

  const handleRegister = (event) => {
    console.debug('Register for event:', event)
  }

  const handleEdit = (event) => {
    console.debug('Edit event:', event)
  }

  return (
    <Container maxWidth="100%">
      <Grid alignItems="center" justifyContent="center" sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Event Display Demo
        </Typography>

        {/* Detailed View Example */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Detailed View
          </Typography>
          <EventDisplay event={{ ...sampleEvent, participantsDetail: true }} mode="detailed" editable={true} onRegister={handleRegister} onEdit={handleEdit} />
        </Box>

        {/* Card View Example with Swiper */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Card View (Swiper)
          </Typography>
          <Box
            sx={{
              position: 'relative',
              '& .swiper': {
                paddingBottom: '40px',
              },
              '& .swiper-button-next, & .swiper-button-prev': {
                color: 'primary.main',
                '&::after': {
                  fontSize: '24px',
                },
              },
              '& .swiper-pagination-bullet': {
                backgroundColor: 'primary.main',
              },
            }}
          >
            <Swiper
              modules={[Navigation, Autoplay, Keyboard, FreeMode, EffectCoverflow]}
              spaceBetween={12}
              slidesPerView="auto"
              freeMode={true}
              navigation
              pagination={{ clickable: true }}
              speed={600}
              autoplay={{
                delay: 6000,
                disableOnInteraction: true,
              }}
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <SwiperSlide key={i} style={{ width: 'auto', height: 'auto' }}>
                  <Box sx={{ width: { xs: '85vw', sm: '400px' }, height: '100%' }}>
                    <EventDisplay
                      event={{
                        ...sampleEvent,
                        participantsDetail: true,
                        resourceId: `${sampleEvent.resourceId}-${i}`,
                        status: ['COMPLETED', 'CANCELLED', 'BOOKED', 'AVAILABLE', 'FULL'][Math.floor(Math.random() * 5)],
                        participantIds: [
                          ...new Set(Array.from({ length: Math.floor(Math.random() * 4) + 3 }, () => `member-${String(Math.floor(Math.random() * 20) + 1).padStart(3, '0')}`)),
                        ],
                      }}
                      mode="card"
                      editable={true}
                      onRegister={handleRegister}
                      onEdit={handleEdit}
                    />
                  </Box>
                </SwiperSlide>
              ))}
              <SwiperSlide style={{ width: 'auto', height: 'auto' }}>
                <Box
                  sx={{
                    width: { xs: '160px', sm: '140px' },
                    height: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                  }}
                >
                  <Card
                    onClick={() => navigate('/wip')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        Show All
                      </Typography>
                      <ArrowForwardIcon
                        sx={{
                          color: 'primary.main',
                          fontSize: '1.2rem',
                        }}
                      />
                    </Box>
                  </Card>
                </Box>
              </SwiperSlide>
            </Swiper>
          </Box>
        </Box>

        {/* Card View Example */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Card View
          </Typography>
          <SimpleHorizontalCards onShowAll={() => navigate('/wip')}>
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <EventDisplay
                key={i}
                event={{
                  ...sampleEvent,
                  resourceId: `${sampleEvent.resourceId}-${i}`,
                  status: ['COMPLETED', 'CANCELLED', 'BOOKED', 'AVAILABLE', 'FULL'][Math.floor(Math.random() * 5)],
                  participantIds: [
                    ...new Set(Array.from({ length: Math.floor(Math.random() * 4) + 3 }, () => `member-${String(Math.floor(Math.random() * 20) + 1).padStart(3, '0')}`)),
                  ],
                }}
                mode="card"
                editable={true}
                onRegister={handleRegister}
                onEdit={handleEdit}
              />
            ))}
          </SimpleHorizontalCards>
        </Box>

        {/* Small Card View Example */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            History View
          </Typography>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 12, sm: 12, md: 6 }} key={i}>
                <EventDisplay
                  event={{
                    ...sampleEvent,
                    resourceId: `${sampleEvent.resourceId}-small-${i}`,
                    status: ['COMPLETED', 'CANCELLED', 'BOOKED', 'AVAILABLE', 'FULL'][Math.floor(Math.random() * 5)],
                    participantIds: Array.from({ length: Math.floor(Math.random() * 4) + 3 }, () => `member-${String(Math.floor(Math.random() * 20) + 1).padStart(3, '0')}`),
                  }}
                  mode="small"
                  editable={true}
                  onEdit={handleEdit}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Private Session Card Examples */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Private Session Card Examples
          </Typography>

          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            {/* Student View - Available */}
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                Instructor View - Available
              </Typography>
              <PrivateSessionCard
                session={{
                  ...samplePrivateSession,
                  resourceId: 'private-001',
                  status: STATUS.AVAILABLE,
                }}
                viewer="instructor"
              />
              <Typography variant="subtitle1" gutterBottom>
                Student View - Booked
              </Typography>
              <PrivateSessionCard
                session={{
                  ...samplePrivateSession,
                  resourceId: 'private-002',
                  status: STATUS.BOOKED,
                }}
                viewer="student"
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Container>
  )
}
